import { addDays, differenceInMinutes, format, startOfWeek } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import type { AgendaAppointment } from '@agenda-facil/contracts';
import type { BarberRecord } from '../onboarding/types';

export type DashboardPeriod = 'today' | 'week';

export interface DashboardMetrics {
  revenueCents: number;
  appointments: number;
  completed: number;
  canceled: number;
  noShow: number;
  newCustomers: number;
  averageTicketCents: number;
}

export interface DashboardRange {
  from: string;
  to: string;
}

export interface DashboardSeriesRange extends DashboardRange {
  id: string;
  label: string;
  date: string;
}

export interface DashboardPeriodConfig {
  dates: string[];
  summary: DashboardRange;
  series: DashboardSeriesRange[];
}

export interface ChartPoint {
  label: string;
  value: number;
}

const activeStatuses = new Set(['PENDING', 'CONFIRMED', 'IN_SERVICE', 'COMPLETED']);

function dateAnchor(date: string): Date {
  return new Date(`${date}T12:00:00Z`);
}

function dateString(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function clock(minute: number): string {
  if (minute === 24 * 60) return '00:00';
  return `${Math.floor(minute / 60)
    .toString()
    .padStart(2, '0')}:${(minute % 60).toString().padStart(2, '0')}`;
}

function zonedRange(
  date: string,
  timezone: string,
  startMinute = 0,
  endMinute = 24 * 60,
): DashboardRange {
  const start = fromZonedTime(`${date}T${clock(startMinute)}:00`, timezone);
  const endDate = endMinute === 24 * 60 ? dateString(addDays(dateAnchor(date), 1)) : date;
  const end = fromZonedTime(`${endDate}T${clock(endMinute)}:00`, timezone);
  return { from: start.toISOString(), to: end.toISOString() };
}

export function buildDashboardPeriod(
  period: DashboardPeriod,
  timezone: string,
  now = new Date(),
): DashboardPeriodConfig {
  const today = formatInTimeZone(now, timezone, 'yyyy-MM-dd');
  const todayAnchor = dateAnchor(today);

  if (period === 'today') {
    const bucketStarts = [0, 4, 8, 12, 16, 20];
    const series = bucketStarts.map((hour) => ({
      id: `${today}-${hour}`,
      label: `${hour.toString().padStart(2, '0')}h`,
      date: today,
      ...zonedRange(today, timezone, hour * 60, (hour + 4) * 60),
    }));
    return { dates: [today], summary: zonedRange(today, timezone), series };
  }

  const weekStart = startOfWeek(todayAnchor, { weekStartsOn: 1 });
  const dates = Array.from({ length: 7 }, (_, index) => dateString(addDays(weekStart, index)));
  const series = dates.map((date) => ({
    id: date,
    label: format(dateAnchor(date), 'EEE', { locale: ptBR }).replace('.', ''),
    date,
    ...zonedRange(date, timezone),
  }));
  const lastDate = dates.at(-1) ?? today;
  return {
    dates,
    summary: {
      from: zonedRange(dates[0] ?? today, timezone).from,
      to: zonedRange(lastDate, timezone).to,
    },
    series,
  };
}

export function isActiveAppointment(appointment: AgendaAppointment): boolean {
  return activeStatuses.has(appointment.status);
}

export function calculateOccupancy(
  dates: string[],
  appointments: AgendaAppointment[],
  barbers: BarberRecord[],
): { percentage?: number; occupiedMinutes: number; capacityMinutes: number } {
  const capacityMinutes = dates.reduce((total, date) => {
    const dayOfWeek = dateAnchor(date).getUTCDay();
    return (
      total +
      barbers
        .filter((barber) => barber.active)
        .flatMap((barber) => barber.schedules ?? [])
        .filter((schedule) => schedule.active && schedule.dayOfWeek === dayOfWeek)
        .reduce(
          (dailyTotal, schedule) =>
            dailyTotal + Math.max(0, schedule.endMinute - schedule.startMinute),
          0,
        )
    );
  }, 0);

  const occupiedMinutes = appointments
    .filter(isActiveAppointment)
    .reduce(
      (total, appointment) =>
        total +
        Math.max(
          0,
          differenceInMinutes(new Date(appointment.endsAt), new Date(appointment.startsAt)),
        ),
      0,
    );

  if (capacityMinutes === 0) return { occupiedMinutes, capacityMinutes };
  return {
    occupiedMinutes,
    capacityMinutes,
    percentage: Math.min(100, Math.round((occupiedMinutes / capacityMinutes) * 100)),
  };
}

export function buildPeakHours(
  appointments: AgendaAppointment[],
  timezone: string,
  limit = 4,
): ChartPoint[] {
  const counts = new Map<string, number>();
  for (const appointment of appointments.filter(isActiveAppointment)) {
    const hour = formatInTimeZone(appointment.startsAt, timezone, 'HH:00');
    counts.set(hour, (counts.get(hour) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((left, right) => right.value - left.value || left.label.localeCompare(right.label))
    .slice(0, limit);
}

export function formatMinutes(minutes: number): string {
  if (minutes <= 0) return '0 min';
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${remainder} min`;
  return remainder === 0 ? `${hours} h` : `${hours} h ${remainder} min`;
}
